import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  api,
  ApiError,
  type AdminGalleryStats,
  type AdminGallerySyncLine,
  type AdminGalleryUser,
  type GalleryPreviewTuning,
} from '../api/client.ts';
import { formatFileSize, formatLastSeen } from '../lib/format.ts';
import { AdminGalleryChart } from './AdminGalleryChart.tsx';
import '../components/NewChatDialog.css';

/**
 * The «Галерея» tab: how far every phone's camera-roll backup has got, and the
 * one button that gives the disk back.
 *
 * Its own file and its own fetch, like the integrations tab and for a stronger
 * reason: the admin screen polls every three seconds, and this data is measured
 * by walking data/uploads/gallery rather than counted from a column. It loads
 * when the tab is opened and after each change, and never on a timer.
 *
 * **What it shows is chosen, and the choosing is the design.** Every number
 * here used to have a tile and a column of its own — purged rows, tombstoned
 * rows, the leftovers of the old sharded layout — and the page read as an
 * inventory of the schema rather than an answer to a question. It is three
 * questions now, in this order: is it running, how much of the phones is here,
 * and what does that weigh. The rest is either folded into the cell it belongs
 * under or printed once, below, and only while it is not zero.
 *
 * Local Pill and Stat rather than the ones in AdminScreen.tsx: importing them
 * back out of that file would make the two import each other. This is now the
 * second copy of each, which is the signal that the split that file's comment
 * has been promising should take them somewhere shared.
 */
function Pill({ tone, children }: { tone: 'good' | 'warn' | 'neutral'; children: React.ReactNode }) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

/**
 * `words` is for a tile whose value is a phrase rather than a figure — «39
 * минут назад» at 24px display type wraps to two lines and makes its tile a
 * head taller than the row it stands in.
 */
function Stat({
  label,
  value,
  hint,
  words,
}: {
  label: string;
  value: string;
  hint?: string;
  words?: boolean;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className={words ? 'stat-value stat-value-words' : 'stat-value'}>{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  );
}

/** Whether this account has anything at all — a row of zeros needs no buttons. */
function synced(user: AdminGalleryUser): boolean {
  return user.assets > 0 || user.devices > 0;
}

/** «2 устройства» — the only plural on this page, so it lives here. */
function devices(count: number): string {
  const tail = count % 10;
  const teen = count % 100;
  if (tail === 1 && teen !== 11) return `${count} устройство`;
  if (tail >= 2 && tail <= 4 && (teen < 12 || teen > 14)) return `${count} устройства`;
  return `${count} устройств`;
}

/**
 * `20:41:07` — a clock with seconds, which the shared formatters do not have.
 *
 * Everything in `lib/format.ts` is written for a person reading a chat, where
 * the second something was said is noise. A sync journal is the opposite: its
 * lines come a few seconds apart inside a run, and the gaps between them are
 * how «идёт медленно, как задумано» is told from «встало».
 */
function clock(at: number, now = Date.now()): string {
  const date = new Date(at);
  const pad = (value: number) => String(value).padStart(2, '0');
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  const today = new Date(now);
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  return sameDay ? time : `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${time}`;
}

/**
 * The phone's own arithmetic as one phrase: «411 из 542, 131 недоступно».
 *
 * Null rather than a sentence when the phone has never said — «на телефоне
 * ничего нет» and «телефон об этом не говорил» are different findings, and the
 * second one on a page full of zeros is invisible.
 */
function phoneLine(user: {
  onPhone: number | null;
  phoneDone: number | null;
  phoneSkipped: number | null;
}): string | null {
  if (user.onPhone === null) return null;
  const done = user.phoneDone ?? 0;
  const skipped = user.phoneSkipped ?? 0;
  return `${done} из ${user.onPhone}` + (skipped > 0 ? `, ${skipped} недоступно` : '');
}

export function AdminGallery() {
  const [data, setData] = useState<AdminGalleryStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Whose journal is open, if any. See GalleryLogDialog. */
  const [reading, setReading] = useState<AdminGalleryUser | null>(null);
  /**
   * Whether «очистить у всех» has been pressed once already.
   *
   * The two-press pattern the account form uses for taking over a nickname,
   * rather than another confirm(): this is the one action on the page that
   * cannot be taken back, and it deserves the totals it is about to spend
   * written out on screen rather than in a dialog nobody reads. Cleared by any
   * reload, so it cannot survive into a later visit.
   */
  const [armed, setArmed] = useState(false);
  /**
   * Whose chart is drawn, or null for everybody.
   *
   * Held here rather than inside the chart so the table below can set it: the
   * row somebody is reading and the account they want the history of are the
   * same account, and a picker that has to be found again at the top of the
   * panel is a picker nobody uses.
   */
  const [charting, setCharting] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.adminGallery());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить галерею');
    }
    setArmed(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Every purge goes through here: one place to be busy, notice and reload in. */
  async function purge(target: { userId: string } | { all: true }) {
    setBusy(true);
    setNotice(null);
    try {
      const done = await api.purgeGallery(target);
      await load();
      setNotice(
        `Удалено ${done.thumbs + done.previews + done.originals} файлов, ${formatFileSize(done.bytes)}.` +
          (done.skipped > 0 ? ` Пропущено незавершённых оригиналов: ${done.skipped}.` : '') +
          (done.missing > 0 ? ` Не нашлось на диске: ${done.missing}.` : ''),
      );
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Не получилось');
      setBusy(false);
      return;
    }
    setBusy(false);
  }

  if (error && !data) return <div className="admin-error">{error}</div>;
  if (!data) return <div className="admin-section" />;

  const { totals, disk, users } = data;
  const onDisk = disk.thumbs.bytes + disk.originals.bytes;
  const owed = Math.max(0, totals.assets - totals.stored - totals.previews);
  // Only ever printed while it is not zero: three counts that matter on the day
  // somebody purges or migrates and never again. A tile each is how a page
  // stops being read.
  const leftovers = [
    totals.purged > 0 && `очищено записей: ${totals.purged}`,
    totals.deleted > 0 && `помечено удалёнными: ${totals.deleted}`,
    disk.legacy.files > 0 &&
      `старая раскладка: ${formatFileSize(disk.legacy.bytes)} в ${disk.legacy.files} файлах`,
  ].filter((line): line is string => typeof line === 'string');

  return (
    <section className="admin-section">
      <h2 className="admin-section-title">Синхронизация</h2>
      {/* «Миниатюры», not «превью», since the middle tier arrived: the 256px
          tier the sync starts with is the miniature, and «превью» is the
          compressed copy the admin sliders size. */}
      <div className="stat-grid">
        <Stat
          label="Последняя строка"
          value={totals.lastSyncAt === null ? 'никогда' : formatLastSeen(totals.lastSyncAt)}
          hint={totals.devices === 0 ? 'ни одного устройства' : devices(totals.devices)}
          words
        />
        {/* The only figure on this page the server cannot work out for itself,
            and the denominator everything under it needs. */}
        <Stat
          label="На телефонах"
          value={totals.onPhone === null ? '—' : String(totals.onPhone)}
          hint={phoneLine(totals) ?? 'телефоны ещё не считали'}
        />
        <Stat
          label="Миниатюры"
          value={String(totals.assets)}
          hint={`${totals.photos} фото, ${totals.videos} видео`}
        />
        <Stat
          label="Превью"
          value={String(totals.previews)}
          hint={owed > 0 ? `осталось сжать ${owed}` : 'все, которым они нужны'}
        />
        <Stat
          label="Оригиналы"
          value={String(totals.stored)}
          hint={
            totals.requested === 0
              ? 'никто не просил'
              : `запрошено ${totals.requested}, ждём ${totals.pending}`
          }
        />
        <Stat
          label="На диске"
          value={formatFileSize(onDisk)}
          hint={
            `миниатюры и превью ${formatFileSize(disk.thumbs.bytes)}, ` +
            `оригиналы ${formatFileSize(disk.originals.bytes)}`
          }
        />
      </div>

      {leftovers.length > 0 && <p className="admin-note">{leftovers.join(' · ')}.</p>}

      {/* Between the totals and the table on purpose: the tiles say how much is
          here, the chart says when it arrived, and the table says who. */}
      <AdminGalleryChart users={users} userId={charting} onUser={setCharting} />

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Никнейм</th>
              <th>На телефоне</th>
              <th>Миниатюры</th>
              <th>Превью</th>
              <th>Оригиналы</th>
              <th>На диске</th>
              <th>Последняя строка</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.userId}>
                <td>
                  {/* Pressing it again gives the chart back to everybody, so
                      the pair of states is reachable from the same place. */}
                  <button
                    type="button"
                    className="admin-pick"
                    aria-pressed={charting === user.userId}
                    title="Показать на графике"
                    onClick={() => setCharting(charting === user.userId ? null : user.userId)}
                  >
                    {user.username}
                  </button>
                  {user.devices > 0 && <span className="admin-sub">{devices(user.devices)}</span>}
                </td>
                <td>
                  {user.onPhone === null ? (
                    <span className="admin-hosts">не говорил</span>
                  ) : (
                    <>
                      {user.onPhone}
                      <span className="admin-sub">{phoneLine(user)}</span>
                    </>
                  )}
                </td>
                <td>
                  {user.assets}
                  {user.assets > 0 && (
                    <span className="admin-sub">
                      {user.photos} фото, {user.videos} видео
                    </span>
                  )}
                </td>
                {/* Out of the miniatures rather than of the whole library: an
                    asset whose original is here is not owed a preview, so the
                    honest «всё» is assets minus those. The phone works from the
                    same subtraction. */}
                <td>
                  {user.previews === 0 ? (
                    '—'
                  ) : user.previews + user.stored >= user.assets ? (
                    <Pill tone="good">{user.previews}</Pill>
                  ) : (
                    <Pill tone="neutral">
                      {user.previews} из {user.assets}
                    </Pill>
                  )}
                </td>
                {/* Three numbers about one thing, so they are one cell:
                    сколько попросили, сколько доехало, сколько ещё едет. */}
                <td>
                  {user.requested === 0 ? (
                    <span className="admin-hosts">не просили</span>
                  ) : user.pending > 0 ? (
                    <Pill tone="warn">
                      {user.stored} из {user.requested}, ждём {user.pending}
                    </Pill>
                  ) : (
                    <Pill tone="good">
                      {user.stored} из {user.requested}
                    </Pill>
                  )}
                </td>
                <td className="admin-hosts">
                  {formatFileSize(user.disk.thumbs.bytes + user.disk.originals.bytes)}
                  {user.disk.originals.bytes > 0 && (
                    <span className="admin-sub">
                      из них оригиналы {formatFileSize(user.disk.originals.bytes)}
                    </span>
                  )}
                </td>
                {/* The whole point of the journal is that the diagnosis is in
                    which ordinary line came last — so the last one is here, and
                    the thirty before it are one click away rather than an ssh
                    and the queries in docs/GALLERY-DEBUG.md. */}
                <td className="admin-log-cell">
                  {user.lastSyncAt === null ? (
                    <span className="admin-hosts">никогда</span>
                  ) : (
                    <button
                      type="button"
                      className="admin-log-open"
                      onClick={() => setReading(user)}
                      title="Показать последние 30 строк"
                    >
                      <span className="admin-log-when">{formatLastSeen(user.lastSyncAt)}</span>
                      {user.lastSyncText && (
                        <span className="admin-log-last">{user.lastSyncText}</span>
                      )}
                    </button>
                  )}
                </td>
                <td className="admin-row-actions">
                  {synced(user) && (
                    <button
                      type="button"
                      className="settings-secondary"
                      disabled={busy}
                      onClick={() => {
                        if (
                          !confirm(
                            `Удалить с диска файлы «${user.username}»? Записи останутся, ` +
                              'телефон не станет загружать всё заново. Незапрошенные оригиналы ' +
                              'и те, что ещё едут, не трогаем.',
                          )
                        ) {
                          return;
                        }
                        void purge({ userId: user.userId });
                      }}
                    >
                      Очистить диск
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-actions">
        <button
          type="button"
          className={armed ? 'primary-button admin-save' : 'settings-secondary'}
          disabled={busy || totals.assets === 0}
          onClick={() => (armed ? void purge({ all: true }) : setArmed(true))}
        >
          {armed ? 'Точно очистить у всех' : 'Очистить диск у всех'}
        </button>
        {armed && (
          <button type="button" className="settings-secondary" onClick={() => setArmed(false)}>
            Отмена
          </button>
        )}
        {notice && <span className="admin-notice">{notice}</span>}
      </div>

      {armed && (
        <p className="admin-note">
          Уйдёт {formatFileSize(onDisk)} — {disk.thumbs.files + disk.originals.files} файлов у всех
          сразу. Останутся записи в базе: именно они отвечают телефону «уже здесь», поэтому заново
          ничего не загрузится.
        </p>
      )}

      <p className="admin-note">
        Очистка удаляет файлы с диска и оставляет записи — это не «удалить фото», а «освободить
        место». Телефон не станет синхронизироваться заново: он спрашивает, что у сервера уже
        есть, и ответ даёт строка в базе, а не файл. Оригинал, который прямо сейчас загружается,
        не трогаем совсем — его недокачанный файл и есть то место, с которого телефон продолжит.
      </p>

      <GalleryTuning />

      {reading && <GalleryLogDialog user={reading} onClose={() => setReading(null)} />}
    </section>
  );
}

/**
 * The backup's tunables, moved here from the overview tab: the sliders and the
 * numbers they act on are about the same phones the table above lists, and the
 * overview's three-second poll was the wrong host for a form — see the GET
 * route. Self-contained on purpose: its own fetch, its own busy, so dragging a
 * slider cannot collide with a purge in flight above.
 */
interface GalleryTuningDraft {
  previewPhotoKb: string;
  previewVideoKbPerMinute: string;
  previewCloudMbPerDay: string;
  windowBacklogRefreshMin: string;
  windowBacklogProcessingMin: string;
  windowIdleMin: string;
  digestEnabled: boolean;
  digestQuietFromHour: string;
  digestQuietToHour: string;
}

function tuningDraftFrom(tuning: GalleryPreviewTuning): GalleryTuningDraft {
  return {
    previewPhotoKb: String(tuning.previewPhotoKb),
    previewVideoKbPerMinute: String(tuning.previewVideoKbPerMinute),
    previewCloudMbPerDay: String(tuning.previewCloudMbPerDay),
    windowBacklogRefreshMin: String(tuning.windowBacklogRefreshMin),
    windowBacklogProcessingMin: String(tuning.windowBacklogProcessingMin),
    windowIdleMin: String(tuning.windowIdleMin),
    digestEnabled: tuning.digestEnabled,
    digestQuietFromHour: String(tuning.digestQuietFromHour),
    digestQuietToHour: String(tuning.digestQuietToHour),
  };
}

function GalleryTuning() {
  const [tuning, setTuning] = useState<GalleryPreviewTuning | null>(null);
  const [draft, setDraft] = useState<GalleryTuningDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const next = await api.galleryPreviewSettings();
        setTuning(next);
        setDraft((current) => current ?? tuningDraftFrom(next));
      } catch {
        setNotice('Не удалось загрузить настройки');
      }
    })();
  }, []);

  if (!tuning || !draft) {
    return notice ? <p className="admin-note">{notice}</p> : null;
  }

  const tuned = tuning.overridden.length > 0;
  const field = (key: Exclude<keyof GalleryTuningDraft, 'digestEnabled'>) => ({
    value: draft[key],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setDraft({ ...draft, [key]: event.target.value }),
  });

  async function save() {
    if (!draft) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.saveGalleryPreviewSettings({
        previewPhotoKb: Number(draft.previewPhotoKb),
        previewVideoKbPerMinute: Number(draft.previewVideoKbPerMinute),
        previewCloudMbPerDay: Number(draft.previewCloudMbPerDay),
        windowBacklogRefreshMin: Number(draft.windowBacklogRefreshMin),
        windowBacklogProcessingMin: Number(draft.windowBacklogProcessingMin),
        windowIdleMin: Number(draft.windowIdleMin),
        digestEnabled: draft.digestEnabled,
        digestQuietFromHour: Number(draft.digestQuietFromHour),
        digestQuietToHour: Number(draft.digestQuietToHour),
      });
      // Echoed back after clamping, so an out-of-range entry corrects itself.
      setTuning(next);
      setDraft(tuningDraftFrom(next));
      setNotice('Сохранено — телефоны спросят цели на следующем проходе синхронизации');
    } catch {
      setNotice('Не удалось сохранить');
    }
    setBusy(false);
  }

  async function reset() {
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.resetGalleryPreviewSettings();
      setTuning(next);
      setDraft(tuningDraftFrom(next));
      setNotice('Сброшено к значениям из .env');
    } catch {
      setNotice('Не удалось сбросить');
    }
    setBusy(false);
  }

  return (
    <div className="admin-tuning">
      <div className="admin-tuning-head">
        <h3 className="admin-tuning-title">Настройки резервной копии</h3>
        <span className="admin-tuning-hint">
          Средний слой — превью между миниатюрой в 256 px и оригиналом: телефон сам сжимает каждое
          фото и видео до цели, выставленной здесь, и загружает по одному. Ниже — полы под
          заявками на фоновые окна iOS. Всё это телефон спрашивает в начале каждого прохода, так
          что ползунки действуют без перезапуска и без пересборки приложения. Уже загруженные
          превью не пересжимаются.
        </span>
      </div>

      <div className="admin-fields">
        <label className="admin-field">
          <span>Превью фото: {draft.previewPhotoKb} КБ</span>
          <input type="range" min={30} max={1000} step={5} {...field('previewPhotoKb')} />
          <small>
            длинная сторона 1600 px, JPEG подгоняется под цель · из .env:{' '}
            {tuning.envDefaults.previewPhotoKb} КБ
          </small>
        </label>
        <label className="admin-field">
          <span>Минута видео-превью: {draft.previewVideoKbPerMinute} КБ</span>
          <input
            type="range"
            min={5000}
            max={30000}
            step={50}
            {...field('previewVideoKbPerMinute')}
          />
          <small>
            размер кадра телефон выводит из этой же цифры · из .env:{' '}
            {tuning.envDefaults.previewVideoKbPerMinute} КБ
          </small>
        </label>
        <label className="admin-field">
          <span>
            Качать из iCloud:{' '}
            {Number(draft.previewCloudMbPerDay) === 0
              ? 'не качать'
              : `${draft.previewCloudMbPerDay} МБ в день`}
          </span>
          <input type="range" min={0} max={5000} step={50} {...field('previewCloudMbPerDay')} />
          <small>
            бюджет на телефон в сутки — для фото, оригинал которых остался только в облаке · из
            .env: {tuning.envDefaults.previewCloudMbPerDay} МБ
          </small>
        </label>
        <label className="admin-field">
          <span>Окно при недосланном: короткое через {draft.windowBacklogRefreshMin} мин</span>
          <input type="range" min={5} max={720} step={5} {...field('windowBacklogRefreshMin')} />
          <small>
            «не раньше чем» под заявкой на ~30-секундное окно; когда именно дать — решает iOS ·
            из .env: {tuning.envDefaults.windowBacklogRefreshMin} мин
          </small>
        </label>
        <label className="admin-field">
          <span>Окно при недосланном: длинное через {draft.windowBacklogProcessingMin} мин</span>
          <input
            type="range"
            min={5}
            max={720}
            step={5}
            {...field('windowBacklogProcessingMin')}
          />
          <small>
            то же для многоминутного окна (обычно на зарядке с Wi-Fi) · из .env:{' '}
            {tuning.envDefaults.windowBacklogProcessingMin} мин
          </small>
        </label>
        <label className="admin-field">
          <span>Окна, когда всё отправлено: через {draft.windowIdleMin} мин</span>
          <input type="range" min={15} max={1440} step={15} {...field('windowIdleMin')} />
          <small>
            окно впрок — только подхватить новые съёмки; бережёт суточный бюджет окон · из .env:{' '}
            {tuning.envDefaults.windowIdleMin} мин
          </small>
        </label>
        <label className="admin-field">
          <span>
            Сводки в чат: {draft.digestEnabled ? 'включены' : 'выключены'}
          </span>
          <input
            type="checkbox"
            checked={draft.digestEnabled}
            onChange={(event) => setDraft({ ...draft, digestEnabled: event.target.checked })}
          />
          <small>
            сервер сам пишет в чат «Сводка: Т — окно 15:21, +3ф» при событиях — окна, новые
            файлы, ошибки; канал — интеграция с грантом «Сервисные отчёты»
          </small>
        </label>
        <label className="admin-field">
          <span>Сводки молчат с {draft.digestQuietFromHour}:00</span>
          <input type="range" min={0} max={23} step={1} {...field('digestQuietFromHour')} />
          <small>начало тихих часов, по времени сервера</small>
        </label>
        <label className="admin-field">
          <span>…и до {draft.digestQuietToHour}:00</span>
          <input type="range" min={0} max={23} step={1} {...field('digestQuietToHour')} />
          <small>
            первый утренний прогон доскажет ночь одной строкой; одинаковые часы — без тихих часов
          </small>
        </label>
        <div className="admin-field admin-field-readout">
          <span>Библиотека в 20 000 фото</span>
          <strong>
            ≈{Math.round(((Number(draft.previewPhotoKb) || 0) * 20000) / 1024 / 102.4) / 10} ГБ
          </strong>
          <small>только превью фото, без миниатюр и видео</small>
        </div>
        <div className="admin-field admin-field-readout">
          <span>Час видео</span>
          <strong>≈{tuning.videoMbPerHour} МБ</strong>
          <small>на диске сервера, после сжатия на телефоне</small>
        </div>
      </div>

      <div className="admin-actions">
        <button
          type="button"
          className="primary-button admin-save"
          disabled={busy}
          onClick={() => void save()}
        >
          Сохранить
        </button>
        <button
          type="button"
          className="settings-secondary"
          disabled={busy || !tuned}
          onClick={() => void reset()}
        >
          Вернуть значения из .env
        </button>
        {tuned && <Pill tone="warn">переопределено здесь, не из .env</Pill>}
        {notice && <span className="admin-notice">{notice}</span>}
      </div>

      <p className="admin-note">
        100 КБ на фото — это уровень «экономии места» Google Фото: на телефоне и ноутбуке не
        отличить от оригинала, на весь экран монитора — уже можно. 500 КБ на минуту видео — это
        ≈68 кбит/с и маленький кадр: «что на этом видео» видно, смотреть кино так не стоит. Бюджет
        iCloud — про чужой мобильный трафик: при «Оптимизации хранилища» оригинал живёт только в
        облаке, и без разрешения качать такое фото не получает превью никогда; ноль — не качать
        вовсе. Полы окон — «не раньше чем», а не расписание: чаще, чем сочтёт нужным iOS, окна не
        пойдут, но суточный бюджет окон перестаёт сгорать одной утренней серией.
      </p>
    </div>
  );
}

/**
 * The last thirty lines one phone posted, newest first.
 *
 * The report this whole journal exists for is «загрузилось пятьсот превью, и
 * всё», and docs/GALLERY-DEBUG.md answers it with a page of SQL against a
 * database on the host. Almost every one of those questions is answered by the
 * end of one run, which is what this is: no filters, no paging, no run picker —
 * the first look, with that document still there for the second.
 *
 * Fetched on open rather than carried in the table's payload. Thirty lines for
 * every account would be most of a megabyte of JSON on a page that is opened to
 * look at one row, and the stats fetch already walks the disk.
 */
function GalleryLogDialog({ user, onClose }: { user: AdminGalleryUser; onClose: () => void }) {
  const [lines, setLines] = useState<AdminGallerySyncLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Escape closes it, as it closes every other sheet in the app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const answer = await api.adminGalleryLog(user.userId);
        if (alive) setLines(answer.lines);
      } catch (err) {
        if (alive) {
          setError(err instanceof ApiError ? err.message : 'Не удалось прочитать журнал');
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [user.userId]);

  return (
    <div className="dialog-scrim" onMouseDown={onClose}>
      <div
        className="dialog-card admin-log-card"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Журнал синхронизации: ${user.username}`}
      >
        <button
          type="button"
          className="icon-button dialog-close"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <X size={16} />
        </button>

        <h3 className="dialog-title">Журнал: {user.username}</h3>

        {error && <p className="admin-note">{error}</p>}
        {!error && lines === null && <p className="admin-note">Читаем…</p>}
        {lines !== null && lines.length === 0 && (
          <p className="admin-note">Этот телефон ещё ничего не присылал.</p>
        )}

        {lines !== null && lines.length > 0 && (
          <ol className="admin-log admin-log-lines">
            {lines.map((line) => (
              <li key={line.id} className={line.event === 'error' ? 'admin-log-bad' : undefined}>
                <span className="admin-log-time">{clock(line.occurred_at)}</span>
                <code className="admin-log-event">{line.event}</code>
                <span className="admin-log-said">{line.text}</span>
                {/* The numbers the phone stamped on this line rather than on
                    the run: flat `done` against a climbing clock is the whole
                    diagnosis, and it is only visible line by line. */}
                {line.total !== null && (
                  <span className="admin-log-nums">
                    {line.done ?? 0}/{line.total}
                    {line.skipped !== null && line.skipped > 0 && <> ·{line.skipped}</>}
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}

        <p className="admin-note">
          Последние 30 строк, новые сверху. Числа справа — сколько телефон отправил из того, что у
          него есть, и через точку — сколько он не смог прочитать. Дальше только SQL:
          docs/GALLERY-DEBUG.md.
        </p>
      </div>
    </div>
  );
}
